import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, Plus } from "lucide-react";
import { useLang } from "../../i18n/LanguageContext";
import { services as staticServices } from "../../data/services";
import { IMAGES } from "../../lib/constants";
import Reveal from "../shared/Reveal";
import { useServiceOverrides, mergeService } from "../../lib/cms";

const previewImage = {
  ai: IMAGES.neural,
  ecommerce: IMAGES.ecommerce,
  consultancy: IMAGES.exec,
  "digital-marketing": IMAGES.heroBg,
  "web-development": IMAGES.laptop,
  "mobile-apps": IMAGES.laptop,
  branding: IMAGES.transform,
  "digital-transformation": IMAGES.transform,
};

const fallbackFeatures_en = {
  "web-development": ["React / Next.js apps", "Headless CMS", "Performance tuning", "Secure APIs"],
  "mobile-apps": ["iOS & Android", "Cross-platform (RN)", "Offline-first", "App Store launch"],
  "branding": ["Identity systems", "Messaging", "Design systems", "Launch assets"],
  "digital-transformation": ["Legacy migration", "Process automation", "Cloud & infra", "Change management"],
};
const fallbackFeatures_ar = {
  "web-development": ["تطبيقات React / Next.js", "CMS منفصل", "تحسين الأداء", "واجهات API آمنة"],
  "mobile-apps": ["iOS وأندرويد", "متعدد المنصات", "يعمل دون إنترنت", "إطلاق المتجر"],
  "branding": ["أنظمة الهوية", "الرسائل", "أنظمة التصميم", "أصول الإطلاق"],
  "digital-transformation": ["ترحيل الأنظمة القديمة", "أتمتة العمليات", "السحابة والبنية", "إدارة التغيير"],
};

export const ServicesGrid = () => {
  const { t, lang } = useLang();
  const overrides = useServiceOverrides();
  const services = staticServices.map((s) => mergeService(s, overrides[s.slug]));
  const [activeIdx, setActiveIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const sectionRef = useRef(null);
  const [inView, setInView] = useState(false);

  // Auto-cycle every 5s while in view + not paused
  useEffect(() => {
    if (!inView || paused) return;
    const id = setInterval(() => {
      setActiveIdx((i) => (i + 1) % services.length);
    }, 5000);
    return () => clearInterval(id);
  }, [inView, paused, services.length]);

  // Observe in-view
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => setInView(e.isIntersecting), { threshold: 0.3 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  // Keyboard navigation
  useEffect(() => {
    if (!inView) return;
    const onKey = (e) => {
      if (e.target && /INPUT|TEXTAREA|SELECT/.test(e.target.tagName)) return;
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIdx((i) => (i + 1) % services.length);
        setPaused(true);
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIdx((i) => (i - 1 + services.length) % services.length);
        setPaused(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [inView, services.length]);

  const active = services[activeIdx];
  const ActiveIcon = active.icon;

  const activeFeatures =
    (lang === "en" ? active.features_en : active.features_ar) ||
    (lang === "en" ? fallbackFeatures_en[active.slug] : fallbackFeatures_ar[active.slug]) ||
    [];

  const activeName = lang === "en" ? active.name_en : active.name_ar;
  const activeShort = lang === "en" ? active.short_en : active.short_ar;

  return (
    <section
      ref={sectionRef}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      className="relative py-20 md:py-32 overflow-hidden"
      id="services"
      data-testid="services-section"
    >
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 md:px-8">
        {/* Header */}
        <Reveal variant="fade-up" className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12 md:mb-16">
          <div className="max-w-2xl">
            <div className="flex items-center gap-2 mb-4">
              <span className="w-8 h-px bg-cyan-400/50" />
              <span className="text-xs mono-label text-cyan-400">{t.services.eyebrow}</span>
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight text-strong leading-[1.05]">
              {t.services.title.split(".").map((part, i) =>
                part.trim() ? (
                  <span key={i} className="block">
                    {part.trim()}
                    {i === 0 && "."}
                    {i === 1 && (
                      <span className="gradient-text">.</span>
                    )}
                  </span>
                ) : null
              )}
            </h2>
            <p className="mt-5 text-base text-mid leading-relaxed max-w-lg">
              {t.services.subtitle}
            </p>
          </div>
          <Link
            to="/services"
            className="inline-flex items-center gap-1.5 text-sm text-cyan-300 hover:text-cyan-200 mono-label whitespace-nowrap"
            data-testid="services-all-link"
          >
            {lang === "en" ? "ALL SERVICES" : "جميع الخدمات"} <ArrowUpRight className="w-4 h-4" />
          </Link>
        </Reveal>

        {/* Split view */}
        <div className="grid lg:grid-cols-12 gap-6 lg:gap-8">
          {/* LEFT — Service list */}
          <div className="lg:col-span-5" data-testid="services-list">
            <div className="flex items-center justify-between mb-4 px-1">
              <span className="text-[10px] mono-label text-faint">
                {lang === "en" ? "DISCIPLINES" : "التخصصات"}
              </span>
              <span className="text-[10px] mono-label text-faint">
                0{activeIdx + 1} / 0{services.length}
              </span>
            </div>

            <ul className="relative border-t border-hair">
              {services.map((s, idx) => {
                const Icon = s.icon;
                const isActive = idx === activeIdx;
                const name = lang === "en" ? s.name_en : s.name_ar;
                return (
                  <li
                    key={s.slug}
                    data-testid={`service-row-${s.slug}`}
                    onMouseEnter={() => setActiveIdx(idx)}
                    onFocus={() => setActiveIdx(idx)}
                    className={`relative border-b border-hair transition-all duration-500 ${
                      isActive ? "bg-hover" : "hover:bg-subtle"
                    }`}
                  >
                    {/* Active indicator line */}
                    <div
                      className={`absolute left-0 top-0 bottom-0 w-[2px] bg-cyan-400 transition-all duration-500 ${
                        isActive ? "opacity-100 scale-y-100" : "opacity-0 scale-y-0"
                      }`}
                      style={{ boxShadow: isActive ? "0 0 16px rgba(0,229,255,0.6)" : "none" }}
                    />

                    <Link
                      to={`/services/${s.slug}`}
                      data-testid={`service-link-${s.slug}`}
                      className="relative flex items-center gap-4 px-3 sm:px-5 py-5 sm:py-6 group"
                    >
                      <span
                        className={`mono-label text-xs w-9 transition-colors ${
                          isActive ? "text-cyan-300" : "text-faint"
                        }`}
                      >
                        0{idx + 1}
                      </span>

                      <div
                        className={`w-9 h-9 rounded-lg flex items-center justify-center border transition-all duration-500 ${
                          isActive
                            ? "border-cyan-400/60 bg-cyan-400/10 text-cyan-300 glow-cyan"
                            : "border-soft bg-subtle text-mid group-hover:text-cyan-300 group-hover:border-cyan-400/30"
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                      </div>

                      <div className="flex-1 min-w-0">
                        <h3
                          className={`text-lg sm:text-xl md:text-2xl font-semibold tracking-tight transition-colors ${
                            isActive ? "text-strong" : "text-mid group-hover:text-strong"
                          }`}
                        >
                          {name}
                        </h3>
                      </div>

                      <div
                        className={`flex items-center gap-1 text-xs mono-label transition-all duration-300 ${
                          isActive
                            ? "text-cyan-300 translate-x-0 opacity-100"
                            : "text-faint -translate-x-2 opacity-0 group-hover:opacity-100 group-hover:translate-x-0"
                        }`}
                      >
                        <span className="hidden sm:inline">{lang === "en" ? "VIEW" : "استعرض"}</span>
                        <ArrowRight className="w-4 h-4" />
                      </div>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* RIGHT — Preview pane */}
          <div
            className="lg:col-span-7 lg:sticky lg:top-28 lg:self-start"
            data-testid="service-preview"
          >
            <div className="relative rounded-3xl overflow-hidden preview-panel min-h-[540px] md:min-h-[600px]">
              {/* Image layer (crossfade) */}
              <div className="absolute inset-0">
                {services.map((s, idx) => (
                  <img
                    key={s.slug}
                    src={previewImage[s.slug]}
                    alt=""
                    className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${
                      idx === activeIdx ? "opacity-50" : "opacity-0"
                    }`}
                  />
                ))}
                {/* Rich navy gradient overlay (constant across both themes) */}
                <div className="absolute inset-0 bg-gradient-to-br from-[#06172E]/95 via-[#081F3F]/85 to-[#050B1A]/95" />
                {/* Radial cyan glow from top-right */}
                <div className="absolute -top-24 -right-24 w-96 h-96 bg-cyan-500/25 rounded-full blur-[120px]" />
                <div className="absolute -bottom-32 -left-20 w-80 h-80 bg-blue-500/20 rounded-full blur-[120px]" />
              </div>

              {/* Content */}
              <div className="relative z-10 p-8 md:p-12 flex flex-col h-full min-h-[540px] md:min-h-[600px]">
                {/* Top row */}
                <div className="flex items-start justify-between mb-10">
                  <div className="flex items-center gap-3">
                    <div className="text-[10px] mono-label text-cyan-300 px-2 py-1 rounded border border-cyan-400/40 bg-cyan-400/10">
                      {lang === "en" ? "SERVICE" : "خدمة"} · 0{activeIdx + 1}
                    </div>
                    {/* Auto-cycle progress ring */}
                    {!paused && inView && (
                      <svg className="w-5 h-5" viewBox="0 0 20 20">
                        <circle cx="10" cy="10" r="8" fill="none" stroke="rgba(0,229,255,0.2)" strokeWidth="1.5" />
                        <circle
                          key={activeIdx}
                          cx="10"
                          cy="10"
                          r="8"
                          fill="none"
                          stroke="#00E5FF"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeDasharray={2 * Math.PI * 8}
                          strokeDashoffset={2 * Math.PI * 8}
                          transform="rotate(-90 10 10)"
                          style={{ animation: "cycle-ring 5s linear forwards" }}
                        />
                      </svg>
                    )}
                  </div>
                  <div className="relative">
                    <div className="absolute inset-0 bg-cyan-400/30 rounded-2xl blur-xl" />
                    <div className="relative w-16 h-16 rounded-2xl bg-white/10 backdrop-blur-xl border border-cyan-400/50 flex items-center justify-center">
                      <ActiveIcon className="w-7 h-7 text-cyan-300" />
                    </div>
                  </div>
                </div>

                {/* Animated title */}
                <div key={active.slug} className="animate-fade-up">
                  <h3 className="text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight text-white leading-tight mb-5">
                    {activeName}
                  </h3>
                  <p className="text-base md:text-lg text-slate-300 leading-relaxed max-w-lg mb-8">
                    {activeShort}
                  </p>

                  {/* Feature chips */}
                  {activeFeatures.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-10">
                      {activeFeatures.slice(0, 6).map((f, i) => (
                        <span
                          key={i}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/[0.06] backdrop-blur-sm border border-white/15 text-xs text-slate-100 hover:border-cyan-400/50 hover:bg-cyan-400/10 transition"
                          style={{ animationDelay: `${i * 60 + 100}ms` }}
                        >
                          <Plus className="w-3 h-3 text-cyan-300" />
                          {f}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Bottom row — CTA + progress */}
                <div className="mt-auto flex flex-col sm:flex-row gap-5 sm:items-end justify-between">
                  <Link
                    to={`/services/${active.slug}`}
                    data-testid="service-preview-cta"
                    className="inline-flex items-center gap-2 bg-cyan-400 hover:bg-cyan-300 text-slate-950 font-semibold px-5 py-3 rounded-xl w-fit shadow-[0_0_32px_rgba(0,229,255,0.4)] hover:shadow-[0_0_48px_rgba(0,229,255,0.6)] transition-all"
                  >
                    {lang === "en" ? "Explore service" : "استعرض الخدمة"}
                    <ArrowUpRight className="w-4 h-4" />
                  </Link>

                  <div className="flex items-center gap-1.5">
                    {services.map((_, i) => (
                      <button
                        key={i}
                        onMouseEnter={() => setActiveIdx(i)}
                        onClick={() => setActiveIdx(i)}
                        aria-label={`Service ${i + 1}`}
                        data-testid={`service-dot-${i}`}
                        className={`h-1 rounded-full transition-all duration-500 ${
                          i === activeIdx
                            ? "w-8 bg-cyan-400 shadow-[0_0_12px_rgba(0,229,255,0.7)]"
                            : "w-3 bg-white/15 hover:bg-white/30"
                        }`}
                      />
                    ))}
                  </div>
                </div>
              </div>

              {/* Corner decoration */}
              <div className="absolute top-5 right-5 w-3 h-3 border-t-2 border-r-2 border-cyan-400/50" />
              <div className="absolute bottom-5 left-5 w-3 h-3 border-b-2 border-l-2 border-cyan-400/50" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServicesGrid;
